// dependencies
import React, { useMemo, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Pagination, SortChangedEvent, Table } from "@performyard/py-components";

//components
import Button from "../../../../../components/Button";
import PageHeading from "../../../../../components/PageHeading";
import FormsIDidFiltersPanel, { FormsIDidFilters } from "./components/FormsIDidFilterPanel";
import FormsIDidReadableFilters from "./components/FormsIDidReadableFilters";
import FormsIDidTableCols from "./components/FormsIDidTableCols";

//hooks
import { useSearchService, useSearchServicePrefetch } from "../../../../../api/hooks";
import { useIsNewNavigation } from "../../../../../api/hooks/defs/navigation";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";
import useUserReviewsNavLinksAndPermissions from "../../hooks/useUserReviewsNavLinksAndPermissions";

//services
import { SERVICE_FORMS } from "../../../../../api/services/defs/search-service";
import { ReviewForm } from "../../../../../api/services/searchServiceResultTypes/reviewForm";

//utils
import { getFiltersForSearchService } from "./utils";

//page template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
//constants
import { REVIEWS_TABLE_LIMIT, USER_REVIEWS_CONFIG } from "../../constants";

const PAGE_TITLE = "Forms I Did";

const FormsIDid = () => {
	const { activePage, navLinks, employeeId, userCanCreateReviews, userCanAdd360Forms } =
		useUserReviewsNavLinksAndPermissions();
	const isNewNav = useIsNewNavigation();
	const { handleQueryChange } = useSearchQueryParams({ schema: USER_REVIEWS_CONFIG.queryParametersSchema });

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<FormsIDidFilters>({
		defaultSortName: "complete_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		fallbackFilters: {
			status: "any",
		},
	});

	const { data, isLoading } = useSearchService<ReviewForm>({
		service: SERVICE_FORMS,
		filters: getFiltersForSearchService(filters, employeeId, debouncedSearchValue),
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Forms",
	});

	//prefetch 3 pages ahead and 1 back
	useSearchServicePrefetch<ReviewForm>(
		{
			service: SERVICE_FORMS,
			filters: getFiltersForSearchService(filters, employeeId, debouncedSearchValue),
			skip: (pageNumber - 2) * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
			disabled: isLoading,
		},
		pageNumber > 1 ? 1 : 0
	);

	useSearchServicePrefetch<ReviewForm>(
		{
			service: SERVICE_FORMS,
			filters: getFiltersForSearchService(filters, employeeId, debouncedSearchValue),
			skip: pageNumber * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
			disabled: isLoading,
		},
		3
	);

	const forms = data?.results || [];
	const totalForms = data?.total || 0;

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const additionalFiltersCount = Object.keys(filters).length;

	const columnDefs = useMemo(() => FormsIDidTableCols(employeeId), [employeeId]);

	const onSortChanged = (event: SortChangedEvent<ReviewForm>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search forms..."
		/>
	);

	const drawer = (
		<FormsIDidFiltersPanel
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
		/>
	);

	const toolbarInformationRow = (
		<FormsIDidReadableFilters filters={{ status: "any", ...filters }} setFilters={setFilters} />
	);

	const mainContent = (
		<div>
			<Table<ReviewForm>
				autoHeight
				columnSizing="fit-grid"
				rowData={forms}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Forms Found"
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }] : [],
					},
				}}
				animateRows={false}
			/>
			{totalForms > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalForms / REVIEWS_TABLE_LIMIT)}
						onPageChange={(newPage: number) => setPageNumber(newPage)}
					/>
				</div>
			)}
		</div>
	);

	const actionButtons = [
		userCanAdd360Forms && (
			<Button data-testid="create360FormButton" onClick={() => handleQueryChange({ modal: "add360Form" })}>
				Add 360 Form
			</Button>
		),
		userCanCreateReviews && (
			<Button
				data-testid="createReviewButton"
				onClick={() => handleQueryChange({ modal: "createReview" })}
				color="green"
				icon={faPlus}
			>
				Create Review
			</Button>
		),
	];

	return (
		<InfoPageTemplate
			employeeID={employeeId}
			pageTitle={PAGE_TITLE}
			pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			actionButtons={actionButtons}
			mainContent={mainContent}
			drawer={drawer}
			navigationLinks={navLinks}
			activeNavigationLinkKey={activePage}
		/>
	);
};

export default FormsIDid;
